/* ============================================================
   Wishlist for Dawn — core module
   - Stores items in localStorage, namespaced per customer ID
   - Merges a guest wishlist into the customer list on login
   - Handles all [data-wishlist-toggle] buttons via delegation
   - Updates all header count badges [data-wishlist-count]
   - Emits `wishlist:updated` on document for other scripts
   ============================================================ */
(function () {
  'use strict';

  var CFG = window.WishlistConfig || {};
  var CUSTOMER_ID = CFG.customerId || null;
  var GUEST_KEY = 'wishlist_guest';
  var STORAGE_KEY = CUSTOMER_ID ? 'wishlist_customer_' + CUSTOMER_ID : GUEST_KEY;

  /* ---------- storage ---------- */

  function read(key) {
    try {
      var raw = localStorage.getItem(key);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function write(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      /* storage full / private mode — fail silently */
    }
    document.dispatchEvent(
      new CustomEvent('wishlist:updated', { detail: { items: items } })
    );
    updateAllButtons();
    updateCountBadges();
    syncToServer(items);
  }

  /* Hook point for future cross-device sync.
     If you later add an app proxy endpoint that writes a customer
     metafield, POST the items here. Safe no-op until then. */
  function syncToServer(items) {
    if (!CFG.syncEndpoint || !CUSTOMER_ID) return;
    try {
      fetch(CFG.syncEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: CUSTOMER_ID, items: items })
      }).catch(function () {});
    } catch (e) {}
  }

  /* Merge guest list into customer list once, on first load after login */
  function mergeGuestList() {
    if (!CUSTOMER_ID) return;
    var guest = read(GUEST_KEY);
    if (!guest.length) return;
    var current = read(STORAGE_KEY);
    var byId = {};
    current.forEach(function (i) { byId[i.productId] = true; });
    guest.forEach(function (i) {
      if (!byId[i.productId]) current.push(i);
    });
    try { localStorage.removeItem(GUEST_KEY); } catch (e) {}
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(current)); } catch (e) {}
  }

  /* ---------- public API ---------- */

  var Wishlist = {
    getAll: function () {
      return read(STORAGE_KEY);
    },
    count: function () {
      return read(STORAGE_KEY).length;
    },
    has: function (productId) {
      productId = String(productId);
      return read(STORAGE_KEY).some(function (i) {
        return String(i.productId) === productId;
      });
    },
    add: function (item) {
      if (!item || !item.productId || !item.handle) return;
      var items = read(STORAGE_KEY);
      if (this.has(item.productId)) return;
      items.push({
        productId: String(item.productId),
        variantId: item.variantId ? String(item.variantId) : null,
        handle: item.handle,
        addedAt: Date.now()
      });
      write(items);
    },
    remove: function (productId) {
      productId = String(productId);
      var items = read(STORAGE_KEY).filter(function (i) {
        return String(i.productId) !== productId;
      });
      write(items);
    },
    toggle: function (item) {
      if (this.has(item.productId)) {
        this.remove(item.productId);
        return false;
      }
      this.add(item);
      /* Report the REAL stored state — if add() couldn't save (missing
         data, full storage), the heart must not pretend it worked. */
      return this.has(item.productId);
    },
    clear: function () {
      write([]);
    },
    addMany: function (items) {
      var current = read(STORAGE_KEY);
      var byId = {};
      current.forEach(function (i) { byId[i.productId] = true; });
      (items || []).forEach(function (i) {
        if (i && i.productId && i.handle && !byId[String(i.productId)]) {
          current.push({
            productId: String(i.productId),
            variantId: i.variantId ? String(i.variantId) : null,
            handle: i.handle,
            addedAt: Date.now()
          });
          byId[String(i.productId)] = true;
        }
      });
      write(current);
    }
  };

  window.Wishlist = Wishlist;

  /* ---------- UI: toggle buttons ---------- */

  var LABELS = {
    add: (CFG.labels && CFG.labels.add) || 'Add to wishlist',
    saved: (CFG.labels && CFG.labels.saved) || 'Saved',
    addedToast: (CFG.labels && CFG.labels.addedToast) || 'added to wishlist',
    removedToast: (CFG.labels && CFG.labels.removedToast) || 'Removed from wishlist',
    viewWishlist: (CFG.labels && CFG.labels.viewWishlist) || 'View wishlist'
  };
  var WISHLIST_URL = CFG.wishlistUrl || '/pages/wishlist';

  /* ---------- toast ---------- */

  var toastEl = null;
  var toastTimer = null;

  function showToast(message, withLink) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'wishlist-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = '';
    var text = document.createElement('span');
    text.textContent = message;
    toastEl.appendChild(text);
    if (withLink) {
      var link = document.createElement('a');
      link.href = WISHLIST_URL;
      link.textContent = LABELS.viewWishlist;
      toastEl.appendChild(link);
    }
    /* restart the animation even if a toast is already showing */
    toastEl.classList.remove('is-visible');
    void toastEl.offsetWidth;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 3500);
  }

  function setButtonState(btn, saved) {
    btn.classList.toggle('is-saved', saved);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    btn.setAttribute('aria-label', saved ? LABELS.saved : LABELS.add);
    var text = btn.querySelector('[data-wishlist-label]');
    if (text) text.textContent = saved ? LABELS.saved : LABELS.add;
  }

  function updateAllButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      setButtonState(btn, Wishlist.has(btn.dataset.productId));
    });
  }

  function updateCountBadges() {
    var count = Wishlist.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count > 0 ? String(count) : '';
      el.classList.toggle('is-empty', count === 0);
    });
  }

  /* Some themes stretch the product-card link ABOVE the heart button, so
     clicks land on the link even though the heart is visually under the
     cursor. elementsFromPoint sees through the overlay, so we can find the
     heart anyway and cancel the navigation. */
  function findToggle(evt) {
    var btn = evt.target && evt.target.closest
      ? evt.target.closest('[data-wishlist-toggle]')
      : null;
    if (btn) return btn;
    if (typeof document.elementsFromPoint !== 'function') return null;
    if (evt.clientX == null || (evt.clientX === 0 && evt.clientY === 0)) return null;
    var stack = document.elementsFromPoint(evt.clientX, evt.clientY);
    for (var i = 0; i < stack.length; i++) {
      if (stack[i].hasAttribute && stack[i].hasAttribute('data-wishlist-toggle')) {
        return stack[i];
      }
    }
    return null;
  }

  /* Capture phase: runs before the card link's own handlers/navigation */
  document.addEventListener('click', function (evt) {
    var btn = findToggle(evt);
    if (!btn) return;

    if (!btn.dataset.productId || !btn.dataset.productHandle) {
      /* The snippet was rendered with a blank product — nothing can be
         saved. Let the click through and tell the developer why. */
      console.warn(
        '[Wishlist] Heart button has empty data-product-id/data-product-handle. ' +
        'The wishlist-button snippet received a blank product — check the render call ' +
        '(Dawn uses `card_product` in card-product.liquid; older versions use `product_card_product`).',
        btn
      );
      return;
    }

    evt.preventDefault();
    evt.stopPropagation();

    /* If the button lives inside a product form with a variant picker,
       prefer the currently selected variant */
    var variantId = btn.dataset.variantId || null;
    var form = btn.closest('form[action*="/cart/add"], .product');
    if (form) {
      var idInput = form.querySelector('input[name="id"], select[name="id"]');
      if (idInput && idInput.value) variantId = idInput.value;
    }

    var wasSaved = Wishlist.has(btn.dataset.productId);
    var nowSaved = Wishlist.toggle({
      productId: btn.dataset.productId,
      variantId: variantId,
      handle: btn.dataset.productHandle
    });
    setButtonState(btn, nowSaved);
    if (nowSaved) {
      btn.classList.add('wishlist-button--pop');
      setTimeout(function () { btn.classList.remove('wishlist-button--pop'); }, 400);
      showToast((btn.dataset.productTitle || 'Item') + ' ' + LABELS.addedToast, true);
    } else if (wasSaved) {
      showToast(LABELS.removedToast, false);
    }
  }, true);

  /* Re-sync button states when Dawn injects new DOM
     (quick-add modals, filtered collection grids, predictive search) */
  var observer = new MutationObserver(function (mutations) {
    for (var m = 0; m < mutations.length; m++) {
      var added = mutations[m].addedNodes;
      for (var n = 0; n < added.length; n++) {
        var node = added[n];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('[data-wishlist-toggle]')) {
          setButtonState(node, Wishlist.has(node.dataset.productId));
        } else if (node.querySelectorAll) {
          node.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
            setButtonState(btn, Wishlist.has(btn.dataset.productId));
          });
        }
      }
    }
  });

  function init() {
    mergeGuestList();
    updateAllButtons();
    updateCountBadges();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* Keep hearts and the header badge fresh when the page didn't just load:
     - pageshow (persisted): back/forward navigation restored from cache
     - storage: the wishlist changed in ANOTHER tab
     - visibilitychange: returning to a tab left open in the background */
  function refreshUI() {
    updateAllButtons();
    updateCountBadges();
  }
  window.addEventListener('pageshow', function (evt) {
    if (evt.persisted) refreshUI();
  });
  window.addEventListener('storage', function (evt) {
    if (evt.key === STORAGE_KEY) refreshUI();
  });
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) refreshUI();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
